<template>

  <div id="menu-translator" class="ai-importer" v-if="user_confirmed">

    <div class="menu-name" @click="redirectToMenu()">{{ menu.name }}</div>

    <h2>Auto-translate</h2>

    <ErrorNotice v-if="error != ''" :message="error" :action-label="errorAction()" @action="runErrorAction()" />

    <p class="explainer">Translate the items, categories and options on this menu into each of its languages. You'll see every proposed translation and can edit it; nothing is added to your menu until you accept.</p>

    <div class="status" v-if="loading_review">
      <div class="spinner"></div>
      <p>Loading…</p>
    </div>

    <!-- step 1: choose what to check (only when no review is waiting) -->
    <div class="mode-options" v-if="proposal === null && !scanning && !loading_review">

      <label class="mode-option" :class="{ 'mode-option-selected': mode === 'missing' }">
        <input type="radio" value="missing" v-model="mode" />
        <div>
          <p class="mode-title">Apply missing translations</p>
          <p class="mode-description">Only translates text that has no translation yet in a language. Existing translations aren't checked, so this uses fewer AI credits.</p>
        </div>
      </label>

      <label class="mode-option" :class="{ 'mode-option-selected': mode === 'all' }">
        <input type="radio" value="all" v-model="mode" />
        <div>
          <p class="mode-title">Check all translations</p>
          <p class="mode-description">Also reviews every existing translation for mistakes and suggests fixes. Every translation is sent for checking, so this uses more AI credits.</p>
        </div>
      </label>

      <button @click="scan()">Start</button>

    </div>

    <div class="status" v-if="scanning">
      <div class="spinner"></div>
      <p>{{ mode === 'missing' ? 'Translating missing text…' : 'Checking your translations…' }} this can take a few minutes for longer menus.</p>
    </div>
    <p class="navigate-away-note" v-if="scanning">Feel free to navigate away, you can monitor all menu auto-import and translation jobs here: <router-link to="/ai_jobs">Auto-import and translation jobs</router-link></p>


    <p class="ai-cost" v-if="isAdmin() && cost && cost.requests > 0">AI cost: {{ formatAiCost(cost) }}</p>

    <!-- review -->
    <div class="review" v-if="proposal !== null">

      <div class="review-header">
        <p class="review-title">Pending translations</p>
        <p class="review-subtitle">
          Translating from {{ proposal.source_language }} ({{ proposal.mode === 'missing' ? 'missing translations only' : 'all translations checked' }}, {{ formatDate(proposal.created_at) }}).
          Tick the translations you want and edit any of them, then accept or reject them. Your edits are saved as you go, so you can leave and come back.
          <span v-if="proposal.skipped_count > 0">{{ proposal.skipped_count }} {{ proposal.skipped_count === 1 ? 'text' : 'texts' }} couldn't be checked; run auto-translate again after this to retry them.</span>
        </p>
        <p class="save-state" v-if="save_state">{{ save_state }}</p>
      </div>

      <div class="language-tabs" v-if="proposal.languages.length > 1">
        <div class="language-tab" :class="{ 'language-tab-active': language === null }" @click="language = null">All languages</div>
        <div class="language-tab" v-for="lang in proposal.languages" :key="lang.code"
          :class="{ 'language-tab-active': language === lang.code }" @click="language = lang.code">{{ lang.name }}</div>
      </div>

      <p class="no-changes" v-if="visible('missing').length === 0 && visible('improve').length === 0">{{ proposal.mode === 'missing' ? 'Nothing is missing a translation.' : 'All translations look good.' }}</p>

      <div class="change-section" v-for="section in sections" :key="section.status" v-show="visible(section.status).length > 0">

        <div class="section-header">
          <p class="section-title">{{ section.title }} ({{ visible(section.status).length }})</p>
          <p class="section-links">
            <span class="link" @click="setIncluded(section.status, true)">Select all</span> ·
            <span class="link" @click="setIncluded(section.status, false)">Select none</span>
            <template v-if="section.status === 'good'"> · <span class="link" @click="show_good = !show_good">{{ show_good ? 'Hide' : 'Show' }}</span></template>
          </p>
        </div>
        <p class="section-subtitle">{{ section.subtitle }}</p>

        <div v-if="section.status !== 'good' || show_good">

          <div class="entry" v-for="field in visible(section.status)" :key="field.key + field.language"
            :class="{ 'entry-excluded': !field.included }">

            <input type="checkbox" v-model="field.included" class="include-checkbox" @change="markDirty(field)" />

            <div class="entry-fields">

              <p class="context">
                <span class="language-badge">{{ languageName(field.language) }}</span>
                {{ describeField(field) }}<span v-if="field.section && field.section !== field.source"> · {{ field.section }}</span>
              </p>

              <p class="source">{{ field.source }}</p>

              <p class="before" v-if="field.status === 'improve'">{{ field.current }}</p>
              <p class="note" v-if="field.note">{{ field.note }}</p>

              <textarea v-if="field.field === 'description'" v-model="field.proposed" rows="2" @input="edited(field)" :dir="direction(field.language)"></textarea>
              <input v-else type="text" v-model="field.proposed" class="translation-input" @input="edited(field)" :dir="direction(field.language)" />

            </div>

          </div>

        </div>

      </div>

    </div>

    <div class="decision-bar" v-if="proposal !== null && !saving">
      <div class="reject-button" @click="reject()">Reject all</div>
      <div class="accept-button" @click="accept()" :class="{ 'confirm-disabled': includedFields().length === 0 }">
        <img src="@/assets/checkmark_normal.svg" />
        <p>Accept {{ includedFields().length }} {{ includedFields().length === 1 ? 'translation' : 'translations' }}</p>
      </div>
    </div>

    <div class="confirm-changes" v-if="saving">
      <p>{{ saving === 'reject' ? 'Rejecting…' : 'Adding translations to your menu…' }}</p>
    </div>

  </div>

</template>


<script>
import ErrorNotice from './ErrorNotice'
import signedInOnly from './signedInOnly'
import { formatAiCost } from './aiCost'
import { runAiJob } from './aiJob'
import { isRtl } from '@/languages.js'

const AUTOSAVE_DELAY = 1000

const FIELD_NAMES = {
  item: 'Item', category: 'Category', major_category: 'Major category', 'option group': 'Option group', option: 'Option'
}

export default {
  components: { ErrorNotice },
  name: "MenuTranslator",
  data() {
    return {
      menu: {},
      proposal: null,
      mode: 'missing',
      fields: [],
      language: null,
      show_good: false,
      scanning: false,
      loading_review: true,
      saving: false,
      dirty_ids: [],
      autosave_timer: null,
      save_state: '',
      error: '',
      error_code: null,
      cost: null,
      sections: [
        { status: 'missing', title: 'Missing translations', subtitle: 'Proposed translations for text that isn\'t translated yet.' },
        { status: 'improve', title: 'Suggested improvements', subtitle: 'Existing translations that look wrong or incomplete. The current translation is crossed out above the suggestion.' },
        { status: 'good', title: 'Look good', subtitle: 'Existing translations with no problems found. Tick any you edit to save your changes.' }
      ]
    }
  },
  mixins: [signedInOnly],
  beforeDestroy() {
    this.flushEdits() // don't lose edits made just before leaving the page
  },
  methods: {
    onUserConfirmed() {
      this.$http.plain.get('/app_show_menu_v2/' + this.menuId())
        .then(response => { this.menu = response.data.menu })
      this.loadPendingReview()
    },
    // A pending review opens straight away; a running one is waited on again (e.g. after a reload).
    loadPendingReview() {
      this.$http.secured.get(`/app_translation_review/${this.menuId()}`)
        .then(response => {
          const review = response.data.review
          if (review && review.status === 'running') {
            this.mode = review.mode
            this.waitForRun(runAiJob(this, Promise.resolve({ data: { job_id: review.job_id } })))
          } else if (review) {
            this.showReview(review)
          }
        })
        .catch(error => this.setError(error, "Couldn't load pending translations. Please reload the page."))
        .finally(() => { this.loading_review = false })
    },
    showReview(review) {
      this.fields = review.fields.map(field => ({ ...field, proposed: field.proposed || '' }))
      this.proposal = review
      this.cost = review.cost
      this.language = null
    },
    formatAiCost,
    // AI costs are shown to admins only (the server leaves them out for everyone else too)
    isAdmin() {
      return this.$store.getters.getUserType === 'admin'
    },
    menuId() {
      return this.$router.currentRoute.params.menu_id
    },
    scan() {
      this.waitForRun(runAiJob(this, this.$http.secured.post(`/app_propose_translations/${this.menuId()}`, { mode: this.mode })))
    },
    waitForRun(run) {
      this.error = ''
      this.scanning = true
      run
        .then(response => {
          const review = response.data
          if (review.status === 'pending') {
            this.showReview(review)
          } else {
            // Nothing to review (e.g. nothing was missing): the server closed the review already.
            this.cost = review.cost
            this.error = review.mode === 'missing' ? 'Nothing is missing a translation.' : 'No translations to review.'
          }
        })
        .catch(error => {
          const data = error.response && error.response.data
          if (error.response && error.response.status === 409 && data.review) {
            this.showReview(data.review) // a review was already waiting (e.g. started in another tab)
          } else {
            this.setError(error, 'Something went wrong checking your translations. Please try again.')
          }
        })
        .finally(() => { this.scanning = false })
    },
    edited(field) {
      field.included = true
      this.markDirty(field)
    },
    // Edits and ticks are saved to the pending review shortly after they're made, so they survive a reload.
    markDirty(field) {
      if (!this.dirty_ids.includes(field.id)) {
        this.dirty_ids.push(field.id)
      }
      this.save_state = 'Saving…'
      clearTimeout(this.autosave_timer)
      this.autosave_timer = setTimeout(() => this.flushEdits(), AUTOSAVE_DELAY)
    },
    takeDirtyFields() {
      clearTimeout(this.autosave_timer)
      const fields = this.fields
        .filter(field => this.dirty_ids.includes(field.id))
        .map(field => ({ id: field.id, proposed: field.proposed, included: field.included }))
      this.dirty_ids = []
      return fields
    },
    flushEdits() {
      const fields = this.takeDirtyFields()
      if (fields.length === 0 || this.proposal === null) {
        return
      }
      this.$http.secured.patch(`/app_translation_review/${this.menuId()}`, { review_id: this.proposal.review_id, fields })
        .then(() => { this.save_state = this.dirty_ids.length > 0 ? 'Saving…' : 'All changes saved' })
        .catch(error => {
          fields.forEach(field => { if (!this.dirty_ids.includes(field.id)) this.dirty_ids.push(field.id) })
          this.save_state = "Couldn't save your latest changes. They'll be saved when you accept."
          this.setError(error, "Couldn't save your latest changes.")
        })
    },
    visible(status) {
      return this.fields.filter(field => field.status === status && (this.language === null || field.language === this.language))
    },
    setIncluded(status, included) {
      this.visible(status).forEach(field => {
        field.included = included
        this.markDirty(field)
      })
    },
    includedFields() {
      return this.fields.filter(field => field.included && (field.status !== 'good' || field.proposed.trim() !== (field.current || '')))
    },
    languageName(code) {
      const language = this.proposal.languages.find(lang => lang.code === code)
      return language ? language.name : code
    },
    describeField(field) {
      const kind = FIELD_NAMES[field.kind] || field.kind
      return field.field === 'description' ? `${kind} description` : `${kind} name`
    },
    direction(code) {
      return isRtl(code) ? 'rtl' : 'ltr'
    },
    formatDate(value) {
      return value ? new Date(value).toLocaleString() : ''
    },
    // Writes the ticked translations to the menu (including any edits not autosaved yet) and closes the review.
    accept() {
      if (this.includedFields().length === 0) {
        return
      }
      const fields = this.takeDirtyFields()
      this.error = ''
      this.saving = 'accept'
      runAiJob(this, this.$http.secured.post(`/app_apply_translations/${this.menuId()}`, { review_id: this.proposal.review_id, fields }))
        .then(() => this.redirectToMenu())
        .catch(error => {
          this.saving = false
          fields.forEach(field => this.dirty_ids.push(field.id))
          this.setError(error, "Couldn't save the translations. Please try again.")
        })
    },
    // Discards every pending translation; nothing is added to the menu.
    reject() {
      if (!confirm('Reject all of these translations? None of them will be added to your menu.')) {
        return
      }
      this.takeDirtyFields()
      this.error = ''
      this.saving = 'reject'
      this.$http.secured.post(`/app_reject_translations/${this.menuId()}`, { review_id: this.proposal.review_id })
        .then(() => {
          this.proposal = null
          this.fields = []
          this.save_state = ''
        })
        .catch(error => this.setError(error, "Couldn't reject the translations. Please try again."))
        .finally(() => { this.saving = false })
    },
    // a button under the error, for the errors the user can do something about
    errorAction() {
      return { no_languages: 'Open menu settings', ai_limit_reached: 'Contact us', subscription_expired: 'See plans' }[this.error_code] || ''
    },
    runErrorAction() {
      if (this.error_code === 'ai_limit_reached') {
        this.$router.push('/contact')
      } else if (this.error_code === 'subscription_expired') {
        this.$router.push('/plans')
      } else {
        this.openSettings()
      }
    },
    openSettings() {
      this.$router.push(`/${this.menuId()}/edit`)
    },
    redirectToMenu() {
      this.$router.replace(`/${this.menuId()}`)
    },
    setError(error, text) {
      const data = (error.response && error.response.data) || {}
      this.error = data.error || error.message || text
      this.error_code = data.code || null
      // A failed run can still have used (and been charged for) AI requests.
      if (data.cost) {
        this.cost = data.cost
      }
    }
  }
}
</script>


<style scoped src="./importer.css"></style>

<style scoped>

.save-state {
  font-size: 12px;
  color: dimgray;
  margin: 0px;
}

.decision-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  display: flex;
  flex-direction: row;
  z-index: 200;
  font-weight: 600;
}

.reject-button {
  background: #BE1E2D;
  color: white;
  padding: 20px;
  cursor: pointer;
  flex-shrink: 0;
}

.accept-button {
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
  background: #39B54A;
  color: white;
  padding: 20px;
  cursor: pointer;
}

.accept-button p {
  margin: 0px;
}

.accept-button img {
  width: 20px;
  height: 20px;
  margin-right: 20px;
  filter: brightness(0) invert(1);
}

.mode-options {
  margin-top: 40px;
}

.mode-option {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  box-sizing: border-box;
  padding: 15px;
  margin-bottom: 10px;
  border: 1px solid lightgray;
  background: white;
  cursor: pointer;
}

.mode-option-selected {
  border-color: rgba(39, 170, 225, 1);
  background: rgba(39, 170, 225, 0.1);
}

.mode-option input {
  margin: 4px 15px 0px 0px;
  flex-shrink: 0;
}

.mode-title {
  font-weight: 600;
  margin: 0px 0px 5px 0px;
}

.mode-description {
  font-size: 13px;
  margin: 0px;
}

.mode-options button {
  margin-top: 15px;
}

.no-changes {
  font-weight: 600;
}

.language-tabs {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  margin-bottom: 30px;
}

.language-tab {
  font-size: 13px;
  font-weight: 600;
  padding: 7.5px 15px;
  margin: 0px 10px 10px 0px;
  border: 1px solid lightgray;
  cursor: pointer;
  background: white;
}

.language-tab-active {
  border-color: rgba(39, 170, 225, 1);
  background: rgba(39, 170, 225, 0.1);
}

.change-section {
  margin-bottom: 40px;
}

.section-header {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0px 0px 10px 0px;
}

.section-links {
  font-size: 12.5px;
  margin: 0px 0px 10px 0px;
}

.link {
  font-weight: 600;
  cursor: pointer;
}

.section-subtitle {
  font-size: 13px;
  margin: 0px 0px 10px 0px;
}

.context {
  font-size: 11px;
  color: dimgray;
  margin: 0px 0px 5px 0px;
}

.language-badge {
  font-weight: 600;
  text-transform: uppercase;
  color: #276175;
  margin-right: 5px;
}

.source {
  font-weight: 600;
  margin: 0px 0px 5px 0px;
}

.before {
  font-size: 13px;
  color: #BE1E2D;
  text-decoration: line-through;
  margin: 0px 0px 5px 0px;
}

.note {
  font-size: 12px;
  font-style: italic;
  margin: 0px 0px 5px 0px;
}

.translation-input {
  width: 100%;
}

</style>
